import React, { useEffect, useRef, useState } from "react";
import { Clock, UserPlus, HelpCircle } from "lucide-react";
import { TeamId } from "store/redux/reducers/team/teamConstants";
import { useTeamUsers } from "shared/hooks";
import { useSelector } from "react-redux";
import { RootState } from "store/redux/store";
import { EnvironmentSelector } from '../RevenueAnalytics/Elements/EnvironmentSelection';
import { getProductId } from "utils/localStorage";

interface TeamMember {
  email: string;
  name: string;
  avatar: string;
}

interface SetupHeaderProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  teams?: TeamId[];
  setupTime: number;
  totalSteps: number;
  completedSteps: number;
  onInviteTeam?: (team: TeamId) => void;
  headerAction?: React.ReactNode;
  /** Rendered right-aligned in the meta row (timing + teams row). */
  metaAction?: React.ReactNode;
}

export function SetupHeader({
  icon,
  title,
  description,
  teams,
  setupTime,
  totalSteps,
  completedSteps,
  onInviteTeam,
  headerAction,
  metaAction
}: SetupHeaderProps) {
  const [teamMembers, setTeamMembers] = useState<Record<TeamId, TeamMember[]>>({});

  // Get the teamMap from the Redux state.
  const teamMap = useSelector((state: RootState) => state.teams.teamMap);

  const [getTeamUsers] = useTeamUsers();

  // Stable key: only re-fetch when the sorted team IDs actually change (not on array re-creation).
  const fetchedForTeamsKey = useRef<string | null>(null);

  useEffect(() => {
    if (!teams || teams.length === 0) return;
    const teamsKey = [...teams].sort().join(",");
    if (fetchedForTeamsKey.current === teamsKey) return; // already fetched for this exact set
    fetchedForTeamsKey.current = teamsKey;

    getTeamUsers({
      variables: {
        input: {
          productId: getProductId() as string
        }
      },
      onCompleted: (data) => {
        if (data?.getTeamUsers?.teams) {
          const mappedTeams: Record<TeamId, TeamMember[]> = {};
          data.getTeamUsers.teams.forEach((team) => {
            if (teams.includes(team.teamId as TeamId)) {
              mappedTeams[team.teamId as TeamId] = team.users.map((user) => ({
                email: user.email,
                name: user.name,
                avatar: user.image || "https://via.placeholder.com/40"
              }));
            }
          });
          setTeamMembers(mappedTeams);
        }
      },
      onError: () => { }
    });
  }, [teams, getTeamUsers]);

  return (
    <div>
      <div className="flex gap-4 mb-4 w-full">
        <div className="w-full">
          <div className="flex items-start space-x-4">
            {/* Icon */}
            <div className="p-3 bg-indigo-50 rounded-lg">{icon}</div>

            {/* Title & Description */}
            <div className="flex flex-col gap-3 w-full">
              <div className="flex w-full justify-between items-start gap-4">
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex flex-row gap-2 items-center">
                    <h1 className="text-[24px] font-bold text-gray-900">{title}</h1>
                    <div className="relative group">
                      <HelpCircle className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                        <div className="absolute bottom-0 left-4 transform translate-y-full">
                          <div className="border-4 border-transparent border-t-gray-800"></div>
                        </div>
                        Collaborate with your teams members
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-gray-600">{description}</p>
                    {metaAction && <div className="flex-shrink-0">{metaAction}</div>}
                  </div>
                </div>
                {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
              </div>

              <div className="flex justify-between">
                <div className="flex justify-between gap-6 items-center">
                  {/* Setup Time and Progress Section */}
                  <div className="flex items-center gap-3 bg-light-gray rounded-md">
                    <div className="flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white shadow-sm border border-gray-200 text-gray-700">
                      <Clock className="w-3 h-3 mr-1" />
                      <span>~{setupTime} mins setup</span>
                      <span className="mx-2">•</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#0525f5] rounded-full transition-all duration-300"
                            style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-xs">
                          {completedSteps}/{totalSteps}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Invite Collaborators Section with Border */}
                  <div className="flex items-center gap-2 rounded-lg">
                    {/* Team Chips with Vertical Line Separators */}
                    {teams && teams.length > 0 && (
                      <div className="flex items-center gap-3">
                        {teams.map((team, index) => {
                          const members = teamMembers[team] || [];
                          const teamName = teamMap[team] || team.replace("_", " ");

                          return (
                            <React.Fragment key={team}>
                              {/* Team Chip Button */}
                              <div className="relative group">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onInviteTeam && onInviteTeam(team);
                                  }}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-primaryExtremeLightColor text-[#1556D0] hover:bg-primaryLightColor hover:text-[#1556D0] transition-colors cursor-pointer"
                                >
                                {/* Team Name */}
                                <span className="font-medium">{teamName}</span>

                                {/* Avatars with overlap and tooltip on hover */}
                                {members.length > 0 ? (
                                  <div className="relative group flex items-center">
                                    <div className="flex items-center">
                                      {members.slice(0, 3).map((member, idx) => (
                                        <div
                                          key={idx}
                                          className={`w-6 h-6 rounded-full bg-white flex items-center justify-center text-[10px] text-gray-800 font-semibold border border-gray-200 ${idx !== 0 ? "-ml-2" : ""}`}
                                        >
                                          {member.avatar.includes("placeholder.com") ? (
                                            member.name.charAt(0).toUpperCase()
                                          ) : (
                                            <img
                                              src={member.avatar}
                                              alt={member.name}
                                              className="w-full h-full rounded-full object-cover"
                                            />
                                          )}
                                        </div>
                                      ))}
                                      {members.length > 3 && (
                                        <div className="-ml-2 w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-semibold border border-gray-200">
                                          +{members.length - 3}
                                        </div>
                                      )}
                                    </div>
                                    {/* Tooltip on hover showing up to 5 names - positioned below to avoid clash */}
                                    <div className="absolute left-0 top-full mt-2 w-36 px-2 py-1.5 bg-gray-700 text-white text-xs rounded shadow-lg hidden group-hover:block z-40">
                                      <div className="absolute bottom-full left-4 mb-0">
                                        <div className="border-4 border-transparent border-b-gray-700"></div>
                                      </div>
                                      <ul className="space-y-0.5">
                                        {members.slice(0, 5).map((member, idx) => (
                                          <li key={idx} className="truncate">{member.name}</li>
                                        ))}
                                        {members.length > 5 && (
                                          <li>+{members.length - 5} more</li>
                                        )}
                                      </ul>
                                    </div>
                                  </div>
                                ) : (
                                  <UserPlus className="w-3 h-3 mr-1" />
                                )}
                                </button>
                                {/* Tooltip on hover - positioned above button */}
                                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none whitespace-normal">
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                    <div className="border-4 border-transparent border-t-gray-800"></div>
                                  </div>
                                  Collaborate with your teams members
                                </div>
                              </div>

                              {/* Vertical Divider between buttons (except for last item) */}
                              {index < teams.length - 1 && <div className="border-l border-gray-300 h-6" />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
